import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import { useLinkComponent } from '@marty-js/design/src/utils/component';
import { Cta } from '@marty-js/design/src/molecules/cta';
import { getApiClient } from '@marty-js/api-sdk';
import { sendMail } from '@marty-js/api-sdk/services/tools';
import type { ItemSidebarComponentFeatureType } from '../types';
import { validateEmail } from '../../../utils/utils';

const Wrapper = styled.div`
  margin: 0 var(--spacer-s) var(--spacer-s) var(--spacer-s);
`;
const Title = styled.div`
  font-weight: bold;
  text-align: center;
  padding: var(--spacer-xs) 0;
  font-size: 28px;
  margin-bottom: var(--spacer-xs);
`;

const CtaWrapper = styled.div`
  margin-top: var(--spacer-xs);
`;

const TextSubject = styled.input`
  background: var(--color-alt-4);
  border: solid 1px ${(props) => (props.theme.isDark ? '#555' : '#ccc')};
  margin: 0 0 var(--spacer-s);
  outline: none;
  padding: var(--spacer-xs);
  width: 100%;
`;

const Info = styled.div`
  text-align: center;
  text-align: justify;
  font-size: 12px;
  line-height: 12px;
  padding: var(--spacer-xs) 0;
  a {
    color: var(--link-color);
  }
`;

const Error = styled.div`
  text-align: center;
  color: var(--theme-palette-red);
`;

const Success = styled.div`
  text-align: center;
  color: var(--theme-palette-green);
`;

type FormType = {
  name: string;
  email: string;
  phoneNumber: string;
  softwareName: string;
  softwareLink: string;
};

export const ContactSoftwareRegistry = ({ data }: ItemSidebarComponentFeatureType) => {
  const [formData, setFormData] = useState<FormType>({
    name: '',
    email: '',
    phoneNumber: '',
    softwareName: '',
    softwareLink: '',
  });

  const [displayConfirmation, setDisplayConfirmation] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const setErrorMessageShow = useCallback(
    (errorMsg: string) => {
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(null), 10000);
    },
    [setErrorMessage],
  );

  const handleChange = useCallback(
    (e: any) => {
      const { name, value } = e.target;
      setFormData({
        ...formData,
        [name]: value,
      });
    },
    [formData],
  );

  const t = useTranslation();

  const sendMessageContact = useCallback(async () => {
    const isValid = Object.values(formData).every((value) => typeof value === 'string' && value.trim().length >= 3);
    if (isValid && validateEmail(formData.email)) {
      const apiClient = getApiClient();
      const userToken = localStorage.getItem('actionToken');
      const sent = await sendMail(
        apiClient,
        userToken,
        t('sdk.ContactSoftwareRegistry.subject', formData),
        formData.email,
        t('sdk.ContactSoftwareRegistry.message', formData),
        'business',
      );
      if (sent?.data?.sendMail?.id) {
        setDisplayConfirmation(true);
        setTimeout(() => setDisplayConfirmation(false), 10000);
      } else {
        setErrorMessageShow(t('sdk.ContactSoftwareRegistry.error.send'));
      }
    } else if (!isValid) {
      setErrorMessageShow(t('sdk.ContactSoftwareRegistry.error.fill_field'));
    } else if (!validateEmail(formData.email)) {
      setErrorMessageShow(t('sdk.ContactSoftwareRegistry.error.email_valid'));
    }
  }, [formData, setErrorMessageShow, t]);

  const Link = useLinkComponent();
  const { infoLink } = data.data.data.config;
  const title = t('sdk.ContactSoftwareRegistry.title');
  const texts = t('sdk.ContactSoftwareRegistry.info').split('%textInfoLink%');
  const textInfoLink = t('sdk.ContactSoftwareRegistry.textInfoLink');

  return (
    <Wrapper>
      <Title>{title}</Title>
      {(Object.keys(formData) as Array<keyof FormType>).map((key) => (
        <TextSubject
          placeholder={t(`sdk.ContactSoftwareRegistry.inputLabel.${key}`)}
          name={key}
          key={key}
          type="text"
          value={formData[key]}
          onChange={handleChange}
        />
      ))}
      {errorMessage ? <Error>{errorMessage}</Error> : null}
      {displayConfirmation ? <Success>{t('sdk.ContactSoftwareRegistry.response')}</Success> : null}
      <CtaWrapper>
        <Cta className="mod-blue" onClick={sendMessageContact}>
          {t('sdk.ContactSoftwareRegistry.inputLabel.submit')}
        </Cta>
      </CtaWrapper>
      <Info>
        {texts[0]}
        <Link href={infoLink}>{textInfoLink}</Link>
        {texts[1]}
      </Info>
    </Wrapper>
  );
};

export default ContactSoftwareRegistry;
